import { motion, AnimatePresence } from 'framer-motion';
import { 
  Palette, 
  Code, 
  Database, 
  Lock, 
  Smartphone, 
  Gauge, 
  Globe, 
  Sparkles, 
  FileCode,
  Menu,
  Search,
  Bell,
  Settings,
  Users,
  BarChart2,
  ArrowUp,
  ArrowDown,
  Calendar,
  Clock,
  DollarSign,
  CheckCircle2,
  XCircle,
  AlertCircle,
  X,
  LineChart,
  UserCog,
  PieChart,
  Activity,
  UserPlus,
  Shield,
  Moon,
  Sun,
  LogOut
} from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { useState } from 'react';

const features = [
  {
    icon: <Palette className="w-6 h-6" />,
    title: "Custom Design",
    description: "Unique, brand-aligned designs that stand out"
  },
  {
    icon: <Code className="w-6 h-6" />,
    title: "Modern Stack",
    description: "Built with React, Next.js, and cutting-edge tools"
  },
  {
    icon: <Database className="w-6 h-6" />,
    title: "Robust Backend",
    description: "Secure data management with Supabase"
  },
  {
    icon: <Lock className="w-6 h-6" />,
    title: "Authentication",
    description: "Secure user management and access control"
  }
];

const technologies = [
  { name: "React", icon: <Code className="w-5 h-5" /> },
  { name: "Next.js", icon: <Globe className="w-5 h-5" /> },
  { name: "Supabase", icon: (
    <img 
      src="https://cdn-icons-png.flaticon.com/512/758/758902.png" 
      alt="Supabase"
      className="w-5 h-5"
    />
  ) },
  { name: "Tailwind CSS", icon: <Palette className="w-5 h-5" /> },
  { name: "TypeScript", icon: <FileCode className="w-5 h-5" /> },
  { name: "Mobile-First", icon: <Smartphone className="w-5 h-5" /> },
  { name: "Performance", icon: <Gauge className="w-5 h-5" /> },
  { name: "AI-Enhanced", icon: <Sparkles className="w-5 h-5" /> }
];

const metrics = [
  {
    label: "Total Revenue",
    value: "$128,429",
    change: "+12.5%",
    isPositive: true,
    icon: <DollarSign className="w-5 h-5 text-blue-500" />
  },
  {
    label: "Active Users",
    value: "2,847",
    change: "+8.2%",
    isPositive: true,
    icon: <Users className="w-5 h-5 text-green-500" />
  },
  {
    label: "Avg. Response Time",
    value: "1.2s",
    change: "-15.3%",
    isPositive: true,
    icon: <Clock className="w-5 h-5 text-purple-500" />
  },
  {
    label: "Error Rate",
    value: "0.8%",
    change: "+0.2%",
    isPositive: false,
    icon: <AlertCircle className="w-5 h-5 text-red-500" />
  }
];

const recentActivity = [
  {
    type: "success",
    message: "Payment processed successfully",
    time: "2 min ago",
    icon: <CheckCircle2 className="w-4 h-4 text-green-500" />
  },
  {
    type: "error",
    message: "Failed to sync data",
    time: "15 min ago",
    icon: <XCircle className="w-4 h-4 text-red-500" />
  },
  {
    type: "warning",
    message: "High CPU usage detected",
    time: "1 hour ago",
    icon: <AlertCircle className="w-4 h-4 text-yellow-500" />
  }
];

const chartData = [65, 45, 75, 55, 85, 60, 70];

const NotificationsPanel = ({ onClose }) => {
  const { theme } = useTheme();
  const notifications = [
    {
      id: 1,
      title: "New User Registration",
      message: "John Smith has joined the platform",
      time: "2 minutes ago",
      icon: <UserPlus className="w-4 h-4 text-green-500" />
    },
    {
      id: 2,
      title: "Security Alert",
      message: "Unusual login attempt detected",
      time: "10 minutes ago",
      icon: <Shield className="w-4 h-4 text-red-500" />
    },
    {
      id: 3,
      title: "System Update",
      message: "Platform upgrade completed successfully",
      time: "1 hour ago",
      icon: <CheckCircle2 className="w-4 h-4 text-blue-500" />
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      className={`absolute top-16 right-4 w-80 ${
        theme === 'dark' ? 'bg-gray-800' : 'bg-white'
      } rounded-lg shadow-xl z-50`}
    >
      <div className="p-4 border-b border-gray-700 flex justify-between items-center">
        <h3 className={`font-semibold ${
          theme === 'dark' ? 'text-white' : 'text-gray-900'
        }`}>Notifications</h3>
        <button onClick={onClose}>
          <X className="w-4 h-4 text-gray-500" />
        </button>
      </div>
      <div className="max-h-96 overflow-y-auto">
        {notifications.map((notification) => (
          <div
            key={notification.id}
            className={`p-4 border-b ${
              theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
            } hover:bg-gray-700/10 transition-colors`}
          >
            <div className="flex gap-3">
              {notification.icon}
              <div>
                <h4 className={`text-sm font-medium ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>{notification.title}</h4>
                <p className={`text-sm ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}>{notification.message}</p>
                <span className="text-xs text-gray-500">{notification.time}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </motion.div>
  );
};

const SettingsModal = ({ onClose }) => {
  const { theme, toggleTheme } = useTheme();
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className={`${
          theme === 'dark' ? 'bg-gray-800' : 'bg-white'
        } rounded-xl p-6 max-w-md w-full mx-4`}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-6">
          <h3 className={`text-xl font-semibold ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>Settings</h3>
          <button onClick={onClose}>
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="space-y-6">
          <div className={`flex items-center justify-between p-4 rounded-lg ${
            theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'
          }`}>
            <div className="flex items-center gap-3">
              {theme === 'dark' ? (
                <Moon className="w-5 h-5 text-blue-400" />
              ) : (
                <Sun className="w-5 h-5 text-blue-400" />
              )}
              <div>
                <h4 className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>
                  Theme
                </h4>
                <p className="text-sm text-gray-500">
                  {theme === 'dark' ? 'Dark mode enabled' : 'Light mode enabled'}
                </p>
              </div>
            </div>
            <button
              onClick={toggleTheme}
              className={`px-4 py-2 rounded-lg ${
                theme === 'dark'
                  ? 'bg-gray-600 text-white'
                  : 'bg-gray-200 text-gray-900'
              }`}
            >
              Toggle
            </button>
          </div>

          <button
            className="w-full p-4 rounded-lg bg-red-500/10 text-red-500 hover:bg-red-500/20 transition-colors flex items-center justify-center gap-2"
          >
            <LogOut className="w-5 h-5" />
            Sign Out
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

const AnalyticsTab = () => {
  const { theme } = useTheme();
  
  return (
    <div className="space-y-6">
      <div className={`${
        theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'
      } p-6 rounded-lg`}>
        <h3 className={`text-xl font-semibold mb-4 ${
          theme === 'dark' ? 'text-white' : 'text-gray-900'
        }`}>Performance Analytics</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className={`p-4 rounded-lg ${
            theme === 'dark' ? 'bg-gray-800' : 'bg-white'
          }`}>
            <div className="flex items-center gap-2 mb-2">
              <Activity className="w-5 h-5 text-blue-500" />
              <span className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>
                CPU Usage
              </span>
            </div>
            <div className="text-2xl font-bold text-blue-500">67%</div>
            <div className="h-2 bg-gray-700 rounded-full mt-2">
              <motion.div
                className="h-full bg-blue-500 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: '67%' }}
                transition={{ duration: 1 }}
              />
            </div>
          </div>
          <div className={`p-4 rounded-lg ${
            theme === 'dark' ? 'bg-gray-800' : 'bg-white'
          }`}>
            <div className="flex items-center gap-2 mb-2">
              <LineChart className="w-5 h-5 text-green-500" />
              <span className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>
                Memory
              </span>
            </div>
            <div className="text-2xl font-bold text-green-500">4.2GB</div>
            <div className="h-2 bg-gray-700 rounded-full mt-2">
              <motion.div
                className="h-full bg-green-500 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: '45%' }}
                transition={{ duration: 1 }}
              />
            </div>
          </div>
          <div className={`p-4 rounded-lg ${
            theme === 'dark' ? 'bg-gray-800' : 'bg-white'
          }`}>
            <div className="flex items-center gap-2 mb-2">
              <PieChart className="w-5 h-5 text-purple-500" />
              <span className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>
                Storage
              </span>
            </div>
            <div className="text-2xl font-bold text-purple-500">78%</div>
            <div className="h-2 bg-gray-700 rounded-full mt-2">
              <motion.div
                className="h-full bg-purple-500 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: '78%' }}
                transition={{ duration: 1 }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const SettingsTab = () => {
  const { theme } = useTheme();
  
  return (
    <div className="space-y-6">
      <div className={`${
        theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'
      } p-6 rounded-lg`}>
        <h3 className={`text-xl font-semibold mb-4 ${
          theme === 'dark' ? 'text-white' : 'text-gray-900'
        }`}>System Settings</h3>
        <div className="space-y-4">
          {['Notifications', 'Security', 'Backups', 'API Access'].map((setting, index) => (
            <div
              key={index}
              className={`p-4 rounded-lg ${
                theme === 'dark' ? 'bg-gray-800' : 'bg-white'
              } flex items-center justify-between`}
            >
              <span className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>
                {setting}
              </span>
              <button className="text-blue-500 hover:text-blue-400">
                Configure
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const UsersTab = () => {
  const { theme } = useTheme();
  
  const users = [
    { name: 'John Doe', role: 'Admin', status: 'Active' },
    { name: 'Jane Smith', role: 'User', status: 'Active' },
    { name: 'Mike Johnson', role: 'Editor', status: 'Inactive' },
  ];

  return (
    <div className="space-y-6">
      <div className={`${
        theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'
      } p-6 rounded-lg`}>
        <div className="flex justify-between items-center mb-6">
          <h3 className={`text-xl font-semibold ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>User Management</h3>
          <button className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2">
            <UserPlus className="w-4 h-4" />
            Add User
          </button>
        </div>
        <div className="space-y-4">
          {users.map((user, index) => (
            <div
              key={index}
              className={`p-4 rounded-lg ${
                theme === 'dark' ? 'bg-gray-800' : 'bg-white'
              } flex items-center justify-between`}
            >
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                  <UserCog className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <div className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>
                    {user.name}
                  </div>
                  <div className="text-sm text-gray-500">{user.role}</div>
                </div>
              </div>
              <div className={`px-3 py-1 rounded-full text-sm ${
                user.status === 'Active'
                  ? 'bg-green-500/20 text-green-500'
                  : 'bg-gray-500/20 text-gray-500'
              }`}>
                {user.status}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const WebsiteDemo = () => {
  const { theme } = useTheme();
  const [activeTab, setActiveTab] = useState('overview');
  const [showNotifications, setShowNotifications] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const renderTabContent = () => {
    switch (activeTab) {
      case 'analytics':
        return <AnalyticsTab />;
      case 'settings':
        return <SettingsTab />;
      case 'users':
        return <UsersTab />;
      default:
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {metrics.map((metric, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`${
                    theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'
                  } p-4 rounded-lg`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className={`p-2 rounded-lg ${
                      theme === 'dark' ? 'bg-gray-800' : 'bg-white'
                    }`}>
                      {metric.icon}
                    </div>
                    <div className={`flex items-center gap-1 text-sm ${
                      metric.isPositive ? 'text-green-500' : 'text-red-500'
                    }`}>
                      {metric.isPositive ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />}
                      {metric.change}
                    </div>
                  </div>
                  <div className={`text-2xl font-bold mb-1 ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>
                    {metric.value}
                  </div>
                  <div className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                    {metric.label}
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className={`lg:col-span-2 ${
                theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'
              } p-6 rounded-lg`}>
                <h3 className={`text-lg font-semibold mb-4 ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>Performance Overview</h3>
                <div className="h-[200px] flex items-end gap-2">
                  {chartData.map((height, i) => (
                    <motion.div
                      key={i}
                      className="flex-1 bg-blue-500 rounded-t"
                      initial={{ height: 0 }}
                      animate={{ height: `${height}%` }}
                      transition={{ delay: i * 0.1, duration: 0.5 }}
                    />
                  ))}
                </div>
                <div className="flex justify-between mt-4 text-sm text-gray-500">
                  {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
                    <span key={day}>{day}</span>
                  ))}
                </div>
              </div>

              <div className={`${
                theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'
              } p-6 rounded-lg`}>
                <h3 className={`text-lg font-semibold mb-4 ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>Recent Activity</h3>
                <div className="space-y-4">
                  {recentActivity.map((activity, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.2 }}
                      className="flex items-start gap-3"
                    >
                      {activity.icon}
                      <div>
                        <div className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>
                          {activity.message}
                        </div>
                        <div className={`text-sm ${
                          theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                        }`}>
                          {activity.time}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        );
    }
  };

  return (
    <div className={`${
      theme === 'dark' ? 'bg-gray-800' : 'bg-white'
    } rounded-xl overflow-hidden shadow-xl max-w-4xl mx-auto`}>
      <div className={`${
        theme === 'dark' ? 'bg-gray-900' : 'bg-gray-100'
      } p-4 flex items-center justify-between relative`}>
        <div className="flex items-center gap-4">
          <Menu className={`w-6 h-6 ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
          }`} />
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-blue-500 font-semibold"
          >
            CloudMetrics
          </motion.span>
        </div>
        <div className="flex items-center gap-4">
          <button
            onClick={() => setShowNotifications(!showNotifications)}
            className="relative"
          >
            <Bell className={`w-5 h-5 ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            }`} />
            <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full" />
          </button>
          <button onClick={() => setShowSettings(true)}>
            <Settings className={`w-5 h-5 ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            }`} />
          </button>
          <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center">
            <span className="text-white text-sm">JD</span>
          </div>
        </div>

        <AnimatePresence>
          {showNotifications && (
            <NotificationsPanel onClose={() => setShowNotifications(false)} />
          )}
        </AnimatePresence>
      </div>

      <div className={`flex gap-4 p-4 ${
        theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'
      }`}>
        {['overview', 'analytics', 'settings', 'users'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`capitalize px-4 py-2 rounded-lg transition-colors ${
              activeTab === tab
                ? 'bg-blue-500 text-white'
                : theme === 'dark'
                ? 'text-gray-400 hover:text-white'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      <div className="p-6">
        {renderTabContent()}
      </div>

      <AnimatePresence>
        {showSettings && (
          <SettingsModal onClose={() => setShowSettings(false)} />
        )}
      </AnimatePresence>
    </div>
  );
};

export default function WebDesignSection() {
  const { theme } = useTheme();

  return (
    <section id="web-design-section" className={`py-20 px-4 ${
      theme === 'dark' ? 'bg-black' : 'bg-white'
    }`}>
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-4 mb-8">
          <Code className="w-12 h-12 text-blue-500" />
          <h2 className={`text-3xl font-bold ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>Web Design & Development</h2>
        </div>

        <div className="prose prose-invert max-w-none mb-12">
          <p className={`text-lg ${
            theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
          }`}>
            Transform your digital presence with our custom web design and development services. We create beautiful, high-performance websites with robust backend functionality tailored to your specific needs.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className={`${
                theme === 'dark' ? 'bg-gray-900' : 'bg-white shadow-lg'
              } p-6 rounded-xl`}
            >
              <div className="text-blue-500 mb-4">{feature.icon}</div>
              <h3 className={`text-xl font-semibold mb-2 ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>{feature.title}</h3>
              <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                {feature.description}
              </p>
            </motion.div>
          ))}
        </div>

        <div className="mb-16">
          <WebsiteDemo />
        </div>

        <div className={`${
          theme === 'dark' ? 'bg-gray-900' : 'bg-white shadow-lg'
        } rounded-xl p-8`}>
          <h3 className={`text-2xl font-bold mb-6 ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>Flexible Technology Stack</h3>
          <p className={`${
            theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
          } mb-8`}>
            We adapt our technology choices to match your project requirements, ensuring the perfect solution for your needs.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {technologies.map((tech, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`flex items-center gap-3 ${
                  theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'
                } p-4 rounded-lg`}
              >
                <div className="text-blue-500">{tech.icon}</div>
                <span className={theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}>
                  {tech.name}
                </span>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}